/**
* Options for scrollToHash function
*/
export interface ScrollOptions {
    offset?: number;
    focusElement?: boolean;
    behavior?: ScrollBehavior;
}

// Constants
const DEFAULT_FOCUS_DELAY = 100;
const DEFAULT_SCROLL_DELAY = 100;
const DEFAULT_PADDING = 24;

/**
* Creates default scroll options with sticky components offset
*/
function getDefaultOptions(): ScrollOptions {
    return {
        offset: getStickyOffset() + DEFAULT_PADDING,
        focusElement: true,
        behavior: 'smooth',
    };
}

/**
* Get the offset height of the sticky components
* @returns {number} - The height of the sticky components in pixels
*/
export function getStickyOffset(): number {
    const quickExitBlock = document.querySelector('.quickExitBlock');
    return quickExitBlock ? (quickExitBlock as HTMLElement).offsetHeight : 0;
}

/**
* Smoothly scrolls to an element identified by a hash
* @param {string} hash - The hash selector (e.g., '#section-1')
* @param {ScrollOptions} options - Additional options
*/
export function scrollToHash(hash: string, options: ScrollOptions = {}): void {
    if (!hash) return;

    try {
        // Make sure hash is prefixed with #
        const selector = hash.startsWith('#') ? hash : `#${hash}`;
        const targetElement = document.querySelector(selector);

        if (!targetElement) return;

        // Merge with default options
        const scrollOptions = { ...getDefaultOptions(), ...options };

        // Calculate position accounting for sticky components offset
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition =
            elementPosition + window.pageYOffset - (scrollOptions.offset || 0);

        // Use window.scrollTo for more consistent behavior
        window.scrollTo({
            top: offsetPosition,
            behavior: scrollOptions.behavior || 'smooth',
        });

        // Set focus for accessibility
        if (scrollOptions.focusElement) {
            // Add tabindex temporarily
            targetElement.setAttribute('tabindex', '-1');

            setTimeout(() => {
                // Focus the element
                (targetElement as HTMLElement).focus({ preventScroll: true });

                // Remove focus after a short delay
                setTimeout(() => {
                    // Remove the tabindex attribute
                    targetElement.removeAttribute('tabindex');

                    // Blur the element to remove focus
                    (targetElement as HTMLElement).blur();
                }, DEFAULT_SCROLL_DELAY);
            }, DEFAULT_FOCUS_DELAY);
        }

        // Update URL without triggering another scroll
        if (history.replaceState && window.location.hash !== hash) {
            history.replaceState(null, '', selector);
        }
    } catch (error) {
        console.warn(`Failed to scroll to ${hash}:`, error);
    }
}

/**
* Executes a callback function when the DOM is ready.
* If the DOM is still loading, it adds an event listener for the 'DOMContentLoaded' event.
* If the DOM is already loaded, it executes the callback immediately.
* 
* @param callback - The function to execute when the DOM is ready
*/
function onDomReady(callback: () => void) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback);
    } else {
        // DOM already loaded
        callback();
    }
}

/**
* Prevents default jump and handles smooth scrolling on page load
*/
export function preventJumpAndScroll(): void {
    const hash = window.location.hash;
    if (!hash) return;

    // Temporarily disable smooth scrolling
    document.documentElement.style.scrollBehavior = 'auto';

    // Scroll to top first to undo browser's default jump
    window.scrollTo(0, 0);

    // Delay execution to ensure elements are loaded
    scrollToHash(hash);

    // Restore smooth scrolling behavior
    setTimeout(() => {
        document.documentElement.style.scrollBehavior = 'smooth';
    }, DEFAULT_SCROLL_DELAY);
}

/**
* Initialize scroll handlers for hash navigation
* Call this function in your entry file to set up smooth scrolling
*/
export function initScrollHandlers(): void {
    // Handle clicks on anchor links to prevent default jump
    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a');
        if (!anchor) return;
        const href = anchor.getAttribute('href');
        const pathname = anchor.getAttribute('pathname');
        if (
            href?.startsWith('#') ||
            (href?.includes('#') && pathname === window.location.pathname)
        ) {
            e.preventDefault();
            scrollToHash(href);
        }
    });

    // Handle hashchange events
    window.addEventListener('hashchange', () => {
        if (window.location.hash) {
            scrollToHash(window.location.hash);
        }
    });

    onDomReady(() => {
        Promise.all([
            new Promise<void>((res) => setTimeout(res, DEFAULT_SCROLL_DELAY)),
        ]).then(() => {
            if (window.location.hash) {
                scrollToHash(window.location.hash);
            }
        });
    });
}